import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { ShieldCheck, AlertTriangle, ArrowLeft, CheckCircle2, Eye, Hourglass, UserCheck, Camera } from "lucide-react";
import VoiceTelemetry from "./VoiceTelemetry";

const verdictBadge = (verdict) => {
  const map = {
    Clean: "bg-[#D1FAE5] text-[#065F46] border-[#10B981]",
    Acceptable: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]",
    "Manual Review": "bg-[#FEF3C7] text-[#92400E] border-[#D97706]",
    Failed: "bg-[#FEE2E2] text-[#991B1B] border-[#DC2626]",
  };
  return map[verdict] || "bg-[#F8F9FA] text-[#525252] border-[#E5E7EB]";
};

const PROGRAM_LABELS = {
  vi_competency_assessment: "Compass Assess · Competency Assessment",
  vi_sales_certification_2026: "VI Sales Certification 2026",
};

export function IntegrityList() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [program, setProgram] = useState(""); // "" = All
  const isAdmin = user?.role === "admin";

  const load = (p = program) => {
    const qs = p ? `?program=${encodeURIComponent(p)}` : "";
    return api.get(`/admin/integrity${qs}`).then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);

  const setProgramAndLoad = (p) => { setProgram(p); load(p); };

  if (err) return <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3">{err}</div>;
  if (!data) return <div className="text-center text-[#878787] py-20 text-sm">Loading integrity log…</div>;

  const counts = data.program_counts || {};
  const total = Object.values(counts).reduce((s, v) => s + v, 0);
  const tabs = [
    { key: "", label: "All programmes", count: total },
    { key: "vi_competency_assessment", label: "Competency Assessment", count: counts.vi_competency_assessment || 0 },
    { key: "vi_sales_certification_2026", label: "Sales Cert 2026", count: counts.vi_sales_certification_2026 || 0 },
  ];

  return (
    <div className="space-y-8 fade-in" data-testid="integrity-center">
      <div>
        <div className="overline mb-2 !text-[#EE2737]">AI PROCTORING</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">Integrity Center</h1>
        <p className="text-[#525252] text-sm mt-2 max-w-3xl leading-relaxed">
          Every proctored attempt — webcam frames flagged by AI vision, tab-switch / paste / fullscreen-exit events, microphone noise spikes, and ID-verification verdicts. Each attempt is scored 0–100 with an AI-generated verdict.
        </p>
      </div>

      {/* Programme tabs — keeps the audit trails of the two programmes cleanly separated */}
      <div className="flex flex-wrap gap-2 border-b border-[#E5E7EB] -mb-6" data-testid="integrity-program-tabs">
        {tabs.map((t) => (
          <button
            key={t.key || "all"}
            onClick={() => setProgramAndLoad(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border-b-2 -mb-px flex items-center gap-2 ${
              program === t.key ? "border-[#EE2737] text-[#0A0A0A]" : "border-transparent text-[#878787] hover:text-[#0A0A0A]"
            }`}
            data-testid={`integrity-tab-${t.key || "all"}`}
          >
            {t.label}
            <span className={`badge ${program === t.key ? "badge-info" : "badge-neutral"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Pending overrides */}
      {isAdmin && data.pending_overrides?.length > 0 && (
        <div className="card p-6 border-l-4 !border-[#D97706]" data-testid="pending-overrides">
          <div className="flex items-center gap-2 mb-3">
            <Hourglass size={16} className="text-[#D97706]" />
            <h2 className="font-heading font-bold text-lg">Pending ID-verification overrides</h2>
            <span className="badge badge-info">{data.pending_overrides.length}</span>
          </div>
          <p className="text-xs text-[#525252] mb-4">These participants couldn&apos;t match selfie ↔ VI Employee ID via AI and have requested manual review. Click &quot;Review&quot; to inspect both photos and grant a one-time override.</p>
          <div className="space-y-2">
            {data.pending_overrides.map((p) => (
              <div key={p.assignment_id} className="flex items-center justify-between gap-3 bg-[#FEF3C7] px-4 py-2.5 text-sm" data-testid={`pending-override-row-${p.assignment_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] text-[#92400E] truncate" data-testid={`pending-override-name-${p.assignment_id}`}>
                    {p.participant_name || p.participant_email || "Unknown candidate"}
                  </div>
                  <div className="text-[11px] text-[#92400E]/80 truncate">
                    {p.participant_email && p.participant_name ? `${p.participant_email} · ` : ""}
                    <span className="font-mono">{p.assignment_id}</span>
                  </div>
                </div>
                <Link to={`/admin/integrity/${p.assignment_id}`} className="btn btn-secondary !text-xs flex-shrink-0" data-testid={`review-override-${p.assignment_id}`}>
                  <Eye size={12} /> Review
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice-Match Telemetry — distribution of every voice-biometric check
          so L&D can eyeball genuine vs impostor separation on real pilot
          data before touching the VOICE_MATCH_THRESHOLD env var. */}
      <VoiceTelemetry />

      <div className="card p-0 overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Participant</th>
              <th>Programme</th>
              <th>Integrity Score</th>
              <th>Verdict</th>
              <th>Events</th>
              <th>Flagged frames</th>
              <th>ID</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={8} className="text-center text-[#878787] py-10 text-sm">No proctored attempts yet.</td></tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.assignment_id} data-testid={`integrity-row-${r.assignment_id}`}>
                <td>
                  <div className="font-semibold text-sm">{r.participant_name || "—"}</div>
                  <div className="text-[11px] text-[#878787]">{r.participant_email}</div>
                </td>
                <td>
                  <span
                    className={`badge ${r.program_key === "vi_sales_certification_2026" ? "badge-info" : "badge-neutral"}`}
                    data-testid={`integrity-row-program-${r.assignment_id}`}
                  >
                    {PROGRAM_LABELS[r.program_key]?.split(" · ")[0] || r.program_key}
                  </span>
                </td>
                <td className="font-heading font-black text-xl">{r.score}</td>
                <td>
                  <span className={`badge border ${verdictBadge(r.verdict)}`}>{r.verdict}</span>
                </td>
                <td className="text-sm">{r.total_events}</td>
                <td className="text-sm">{r.suspicious_frame_count}</td>
                <td>
                  {r.id_verified ? (
                    <span className="inline-flex items-center gap-1 text-[#065F46] text-xs"><CheckCircle2 size={12} /> Verified{r.id_override_granted ? " (manual)" : ""}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#991B1B] text-xs"><AlertTriangle size={12} /> Unverified</span>
                  )}
                </td>
                <td>
                  <Link to={`/admin/integrity/${r.assignment_id}`} className="text-[#EE2737] hover:underline text-sm" data-testid={`open-integrity-${r.assignment_id}`}>
                    Open <Eye size={12} className="inline" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function IntegrityDetail() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [diag, setDiag] = useState(null);
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(false);
  const [note, setNote] = useState("");

  const load = () => Promise.all([
    api.get(`/admin/proctor/${assignmentId}`).then((r) => setData(r.data)),
    api.get(`/admin/integrity/diagnostics/${assignmentId}`).then((r) => setDiag(r.data)).catch(() => {}),
  ]).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assignmentId]);

  const grantOverride = async () => {
    setWorking(true);
    try {
      await api.post(`/admin/proctor/${assignmentId}/override`, { note });
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  if (err) return <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3">{err}</div>;
  if (!data) return <div className="text-center text-[#878787] py-20 text-sm">Loading…</div>;

  const integrity = data.integrity;
  const idv = data.id_verification;
  const verdict = idv?.verdict;
  const verified = verdict?.same_person || idv?.override_granted;

  return (
    <div className="space-y-8 fade-in" data-testid="integrity-detail">
      <button onClick={() => navigate("/admin/integrity")} className="btn btn-ghost !text-xs" data-testid="back-to-integrity">
        <ArrowLeft size={12} /> Back to Integrity Center
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Score card */}
        <div className="card p-6 md:col-span-1">
          <div className="overline mb-2">INTEGRITY SCORE</div>
          <div className="font-heading font-black text-6xl">{integrity?.score ?? "—"}<span className="text-2xl text-[#878787]">/100</span></div>
          {integrity?.verdict && (
            <span className={`badge border mt-3 ${verdictBadge(integrity.verdict)}`}>{integrity.verdict}</span>
          )}
          <p className="text-[12.5px] text-[#525252] mt-4 leading-relaxed">{integrity?.summary}</p>
          {data.participant && (
            <div className="border-t border-[#E5E7EB] mt-4 pt-3 text-[12px]">
              <div className="font-semibold">{data.participant.name}</div>
              <div className="text-[#878787]">{data.participant.email}</div>
              <div className="text-[#878787]">{data.participant.role_level} · {data.participant.region}</div>
            </div>
          )}
        </div>

        {/* ID verification card */}
        <div className="card p-6 md:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">ID VERIFICATION</div>
            {verified ? (
              <span className="badge badge-success"><CheckCircle2 size={11} /> Verified{idv?.override_granted ? " · Manual override" : ""}</span>
            ) : (
              <span className="badge" style={{ background: "#FEE2E2", color: "#991B1B" }}><AlertTriangle size={11} /> Not verified</span>
            )}
          </div>

          {!idv && <p className="text-sm text-[#878787]">No ID verification recorded for this attempt.</p>}

          {idv && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="overline mb-2">SELFIE</div>
                  {idv.selfie_base64 ? (
                    <img src={`data:image/jpeg;base64,${idv.selfie_base64}`} alt="selfie" className="w-full aspect-[4/3] object-cover border border-[#E5E7EB]" data-testid="selfie-image" />
                  ) : (
                    <div className="aspect-[4/3] bg-[#F8F9FA] flex items-center justify-center text-[11px] text-[#878787] border border-[#E5E7EB]">Image not available</div>
                  )}
                </div>
                <div>
                  <div className="overline mb-2">VI EMPLOYEE ID</div>
                  {idv.id_card_base64 ? (
                    <img src={`data:image/jpeg;base64,${idv.id_card_base64}`} alt="ID" className="w-full aspect-[4/3] object-contain bg-[#F8F9FA] border border-[#E5E7EB]" data-testid="id-image" />
                  ) : (
                    <div className="aspect-[4/3] bg-[#F8F9FA] flex items-center justify-center text-[11px] text-[#878787] border border-[#E5E7EB]">Image not available</div>
                  )}
                </div>
              </div>

              {verdict && (
                <div className="bg-[#F8F9FA] p-3 text-[12.5px] leading-relaxed border-l-2 border-[#0A0A0A]">
                  <div><b>Same person:</b> {String(verdict.same_person)} · <b>Confidence:</b> {(verdict.confidence ?? 0).toFixed(2)} · <b>ID type:</b> {verdict.id_type || "unknown"} · <b>Genuine:</b> {String(verdict.id_looks_genuine ?? "—")}</div>
                  {verdict.reasoning && <div className="mt-1.5 text-[#525252]">{verdict.reasoning}</div>}
                </div>
              )}

              {/* Override controls — admin only */}
              {isAdmin && !idv.override_granted && !verified && (
                <div className="mt-4 p-3 bg-[#FEF3C7] border-l-4 border-[#D97706]" data-testid="override-panel">
                  <div className="text-[12px] text-[#92400E] font-semibold mb-2">Grant manual ID-verification override</div>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional admin note (e.g. verified via Zoom call)"
                    className="input !text-xs mb-2"
                    data-testid="override-note"
                  />
                  <button onClick={grantOverride} disabled={working} className="btn btn-primary !text-xs" data-testid="grant-override">
                    <UserCheck size={12} /> {working ? "Granting…" : "Grant override · admit participant"}
                  </button>
                </div>
              )}

              {idv.override_granted && (
                <div className="mt-4 p-3 bg-[#D1FAE5] text-[#065F46] text-[12px] border-l-4 border-[#10B981]" data-testid="override-granted-notice">
                  <b>Manual override granted</b> by {idv.override_by} at {idv.override_at}
                  {idv.override_note ? ` — "${idv.override_note}"` : ""}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Analyser diagnostic strip — proves the proctoring pipeline was running */}
      {diag && (
        <div
          className={`card p-6 border-l-4 ${diag.analyser_healthy ? "border-[#10B981]" : "border-[#EE2737]"}`}
          data-testid="integrity-diagnostics"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="overline">PROCTOR ANALYSER · DIAGNOSTIC</div>
              <h3 className="font-heading font-bold text-lg">
                {diag.analyser_healthy
                  ? "✓ Analyser was running for this attempt"
                  : "⚠ No frames captured — analyser was not running"}
              </h3>
            </div>
            <div className="text-[11px] text-[#525252] max-w-md text-right">
              {diag.analyser_healthy
                ? "Webcam frames were captured periodically and analysed by Gemini vision. Any anomalies would fire proctor events (right)."
                : "Frames total = 0. Ask the candidate to check webcam/mic permissions and retest."}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#E5E7EB]">
            <div className="bg-white p-4">
              <div className="overline mb-1">FRAMES CAPTURED</div>
              <div className="text-3xl font-heading font-black" data-testid="diag-frames-total">{diag.frames_total}</div>
              <div className="text-[10px] text-[#878787] mt-1">All webcam snapshots</div>
            </div>
            <div className="bg-white p-4">
              <div className="overline mb-1 !text-[#EE2737]">SUSPICIOUS FRAMES</div>
              <div className="text-3xl font-heading font-black text-[#EE2737]" data-testid="diag-frames-suspicious">{diag.frames_suspicious}</div>
              <div className="text-[10px] text-[#878787] mt-1">Gemini flagged as anomaly</div>
            </div>
            <div className="bg-white p-4">
              <div className="overline mb-1">TOTAL EVENTS</div>
              <div className="text-3xl font-heading font-black" data-testid="diag-events-total">{diag.events_total}</div>
              <div className="text-[10px] text-[#878787] mt-1">Tab-blur, paste, noise, etc.</div>
            </div>
            <div className="bg-white p-4">
              <div className="overline mb-1">LAST ANALYSED</div>
              <div className="text-[13px] font-mono font-bold" data-testid="diag-last-analysed">
                {diag.last_analysed_at ? new Date(diag.last_analysed_at).toLocaleString() : "—"}
              </div>
              <div className="text-[10px] text-[#878787] mt-1">Most recent frame timestamp</div>
            </div>
          </div>

          {Object.keys(diag.events_by_type || {}).length > 0 && (
            <div className="mt-5">
              <div className="overline mb-2">EVENTS BY TYPE</div>
              <div className="flex flex-wrap gap-2" data-testid="diag-events-by-type">
                {Object.entries(diag.events_by_type).map(([t, n]) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#F5F5F5] text-[#0A0A0A] border border-[#E5E7EB]"
                    data-testid={`diag-event-${t}`}
                  >
                    {t.replace(/_/g, " ")}
                    <span className="text-[#EE2737]">×{n}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Impostor / candidate-absent call-out — Sarvam diarisation + voice-match findings */}
          {(diag.recent_events || []).filter((e) => ["multiple_speakers_detected", "candidate_voice_absent"].includes(e.event_type)).length > 0 && (
            <div className="mt-5 bg-[#FEE2E2] border border-[#FCA5A5] p-4" data-testid="diag-impostors">
              <div className="overline mb-2 !text-[#991B1B]">VOICE INTEGRITY FLAGS</div>
              <ul className="space-y-2 text-[11px]">
                {(diag.recent_events || [])
                  .filter((e) => ["multiple_speakers_detected", "candidate_voice_absent"].includes(e.event_type))
                  .map((e, i) => {
                    const absent = e.event_type === "candidate_voice_absent";
                    return (
                    <li key={i} className="border-l-4 border-[#EE2737] pl-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[#991B1B]">
                        {absent ? "CANDIDATE VOICE ABSENT" : "SECOND VOICE"} · {(e.detail?.source || "audio").replace(/_/g, " ")} · {new Date(e.occurred_at || e.created_at).toLocaleString()}
                      </div>
                      <div className="mt-1 text-[#0A0A0A]">
                        {absent
                          ? <>Top similarity <b className="font-mono">{e.detail?.top_similarity ?? "—"}</b> is below the impostor threshold (<b className="font-mono">{e.detail?.threshold}</b>) — no speaker on this slice matches the enrolled voice.</>
                          : <><b>{e.detail?.speaker_count || "?"}</b> distinct voices in this slice.
                              {e.detail?.candidate_speaker
                                ? <>&nbsp;Candidate = <b className="font-mono">{e.detail.candidate_speaker}</b>; impostor
                                    = <b className="font-mono text-[#991B1B]">{(e.detail.impostor_speakers || []).join(", ")}</b>.</>
                                : <>&nbsp;Voice-match couldn&apos;t label either speaker (no enrolment or too little audio).</>}</>}
                      </div>
                      {e.detail?.speaker_similarity && Object.keys(e.detail.speaker_similarity).length > 0 && (
                        <div className="mt-1 text-[10px] text-[#525252] font-mono">
                          Similarity vs enrolled voice:&nbsp;
                          {Object.entries(e.detail.speaker_similarity).map(([spk, sim]) => (
                            <span key={spk} className="mr-3">
                              {spk}={sim === null ? "—" : sim}
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                    );
                  })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Suspicious frames */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">WEBCAM EVIDENCE</div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Camera size={16} /> Flagged frames ({data.frames?.length || 0})</h3>
          </div>
          <p className="text-[11px] text-[#878787]">Only frames with anomalies are persisted. Resolution: 480px wide.</p>
        </div>
        {(!data.frames || data.frames.length === 0) ? (
          <p className="text-sm text-[#878787]">No suspicious frames during this attempt.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {data.frames.map((f) => (
              <div key={f.id} className="border border-[#E5E7EB]">
                {f.image_base64 ? (
                  <img src={`data:image/jpeg;base64,${f.image_base64}`} alt="frame" className="w-full aspect-[4/3] object-cover" />
                ) : (
                  <div className="aspect-[4/3] bg-[#F8F9FA] flex items-center justify-center text-[10px] text-[#878787]">no image</div>
                )}
                <div className="p-2 text-[10px] bg-[#F8F9FA]">
                  <div className="font-bold">{f.analysis?.face_count ?? "?"} face(s)</div>
                  {(f.analysis?.anomalies || []).slice(0,2).map((a, i) => (
                    <div key={i} className="text-[#878787] truncate">• {a}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Event log */}
      <div className="card p-6">
        <div className="overline mb-2">EVENT LOG</div>
        <h3 className="font-heading font-bold text-lg mb-3">Audit timeline ({data.events?.length || 0})</h3>
        {(!data.events || data.events.length === 0) ? (
          <p className="text-sm text-[#878787]">No proctoring events recorded.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => {
                  // iter53 — flagged webcam-frame events (face_away,
                  // multiple_faces, face_absent, voice_enrol_face_mismatch)
                  // carry a `frame_base64` / `live_selfie_base64` payload
                  // on their detail. Render it as a thumbnail tile next to
                  // the reasoning so admins see WHO/WHAT triggered the
                  // flag without opening a second screen.
                  const detail = e.detail || {};
                  const frameB64 = detail.frame_base64 || detail.live_selfie_base64;
                  const { frame_base64: _f, live_selfie_base64: _l, ...scalarDetail } = detail;
                  const imgSrc = frameB64
                    ? (String(frameB64).startsWith("data:")
                        ? String(frameB64)
                        : `data:image/jpeg;base64,${String(frameB64).trim()}`)
                    : null;
                  return (
                    <tr key={e.id}>
                      <td className="text-[11px] font-mono whitespace-nowrap align-top">{new Date(e.occurred_at || e.created_at).toLocaleString()}</td>
                      <td className="align-top"><span className={`badge ${eventTone(e.event_type)}`}>{e.event_type.replace(/_/g, " ")}</span></td>
                      <td className="text-[11px] text-[#525252] align-top">
                        <div className="flex items-start gap-3">
                          {imgSrc && (
                            <figure className="flex-shrink-0" data-testid={`event-frame-${e.id}`}>
                              <img
                                src={imgSrc}
                                alt={`${e.event_type} capture`}
                                className="w-24 h-24 object-cover border border-[#E5E7EB] bg-black"
                                onError={(ev) => {
                                  const el = ev.currentTarget;
                                  el.style.display = "none";
                                  const fb = el.nextElementSibling;
                                  if (fb) fb.style.display = "flex";
                                }}
                              />
                              <div
                                className="w-24 h-24 border border-[#DC2626] bg-[#FEF2F2] text-[10px] text-[#991B1B] p-1 items-center justify-center text-center leading-tight font-mono"
                                style={{ display: "none" }}
                              >
                                Image unreadable
                              </div>
                              <figcaption className="text-[9px] uppercase tracking-widest text-[#878787] mt-0.5 font-bold">
                                Captured frame
                              </figcaption>
                            </figure>
                          )}
                          <div className="min-w-0 flex-1 break-words">
                            {Object.keys(scalarDetail).length > 0
                              ? JSON.stringify(scalarDetail)
                              : <span className="italic text-[#878787]">—</span>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {integrity?.event_counts && Object.keys(integrity.event_counts).length > 0 && (
        <div className="card p-6">
          <div className="overline mb-2">EVENT BREAKDOWN</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(integrity.event_counts).map(([k, v]) => (
              <div key={k} className="px-3 py-1.5 bg-[#F8F9FA] text-[11px] border border-[#E5E7EB]">
                <span className="font-bold uppercase tracking-wider">{k.replace(/_/g, " ")}</span>
                <span className="ml-2 font-heading font-black">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function eventTone(t) {
  if (["multiple_faces", "face_absent", "face_away", "identity_mismatch", "voice_enrol_face_mismatch", "face_recheck_fail"].includes(t)) return "badge-red";
  if (["tab_blur", "fullscreen_exit", "paste_blocked", "typing_burst"].includes(t)) return "badge-info";
  if (["manual_flag", "auto_submitted"].includes(t)) return "badge-neutral";
  return "badge-neutral";
}
